/**
 * Os quatro "Excel em lote" do menu antigo, e a sugestão de apelido.
 *
 * **Confere antes de aplicar, e é o ponto da tela.** O `/conferir` roda a
 * planilha inteira **sem gravar** e devolve linha a linha o que aconteceria; o
 * `/aplicar` faz. No sistema antigo existe só o segundo: manda a planilha e
 * descobre depois.
 *
 * **A rota é do projeto de propósito.** O `UPDATE` do legado é
 * `WHERE componente_id = @id` e mais nada — uma planilha com um id de outro
 * projeto altera o componente de outro projeto em silêncio. Aqui id de fora vira
 * linha recusada com o motivo, contada em `foraDoProjeto`.
 *
 * **A linha 1 é cabeçalho e é pulada.** O legado tem o comentário
 * "//Pula a linha de titulo" logo acima de um laço que começa em zero — ou seja,
 * não pula: tenta alterar o componente de id "Código" e não estoura porque o
 * `UPDATE` não casa com nada.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

/** O que a planilha altera. Vai por texto, não pelo número do enum. */
export const CAMPOS_DO_LOTE = [
  { chave: "apelido", rotulo: "Apelido do componente" },
  { chave: "descricao", rotulo: "Descrição do componente" },
  { chave: "localizacao", rotulo: "Localização do componente" },
  { chave: "pino", rotulo: "Pino do pinout" },
] as const

export type CampoDoLote = (typeof CAMPOS_DO_LOTE)[number]["chave"]

export type LinhaDoLote = {
  linha: number
  id: string
  valor: string
  /** vai mudar · já está assim · é de outro projeto · não existe · linha incompleta */
  situacao: string
  valorAtual: string
}

export type ConferenciaDoLote = {
  projetoId: number
  campo: string
  linhasNaPlanilha: number
  vaoMudar: number
  jaEstaoAssim: number
  foraDoProjeto: number
  naoExistem: number
  semDados: number
  linhas: LinhaDoLote[]
}

export type ResultadoDoLote = {
  projetoId: number
  campo: string
  alterados: number
  conferencia: ConferenciaDoLote
}

/**
 * Confere ou aplica. É a **mesma chamada**, e o que muda é o endereço.
 *
 * A trava do canário deixa o `/conferir` passar — ela está na lista de
 * conferências —, e barra o `/aplicar` fora do projeto canário. É o desenho
 * certo: a conferência existe justamente para ver o estrago antes.
 */
async function enviar(
  acao: "conferir" | "aplicar",
  p: { projetoId: number; campo: CampoDoLote; colunaId: number; colunaValor: number; arquivo: File },
) {
  const corpo = new FormData()
  corpo.append("campo", p.campo)
  corpo.append("colunaId", String(p.colunaId))
  corpo.append("colunaValor", String(p.colunaValor))
  corpo.append("arquivo", p.arquivo)

  const r = await api.post<ConferenciaDoLote | ResultadoDoLote>(
    `api/projetos/${p.projetoId}/importacao/${acao}`, corpo, noProjeto(p.projetoId))
  return r.data
}

/**
 * Baixa a planilha de apelidos do projeto: id | código | descrição | apelido,
 * linha 1 de cabeçalho. Preenche-se a coluna apelido e manda-se o mesmo arquivo
 * de volta no lote (id na coluna 1, valor na coluna 4).
 */
export async function baixarPlanilhaDeApelidos(projetoId: number): Promise<{ nome: string; bytes: number }> {
  const r = await api.get<Blob>(
    `api/projetos/${projetoId}/importacao/planilha-de-apelidos`,
    { ...noProjeto(projetoId), responseType: "blob" })

  // O nome vem no Content-Disposition; sem ele o navegador salva "planilha-de-apelidos".
  const disp = String(r.headers?.["content-disposition"] ?? "")
  const achado = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp)
  const nome = achado ? decodeURIComponent(achado[1]!) : `apelidos-projeto-${projetoId}.xlsx`

  const url = URL.createObjectURL(r.data)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)

  return { nome, bytes: r.data.size }
}

export function usarConferirLote() {
  return useMutation({
    mutationFn: (p: Parameters<typeof enviar>[1]) =>
      enviar("conferir", p) as Promise<ConferenciaDoLote>,
  })
}

export function usarAplicarLote() {
  return useMutation({
    mutationFn: (p: Parameters<typeof enviar>[1]) =>
      enviar("aplicar", p) as Promise<ResultadoDoLote>,
  })
}

// ------------------------------------------------------------------ apelidos

export type SugestaoDeApelido = {
  componenteId: number
  codigo: string
  descricao: string
  apelido: string
  /** Apelidos de projetos **da mesma marca**. Mais confiável. */
  porCodigo: string[]
  /** De qualquer projeto, pela descrição. Mais farta, menos confiável. */
  porDescricao: string[]
}

export type SugestoesDoProjeto = {
  projetoId: number
  componentes: number
  semApelido: number
  comSugestao: number
  itens: SugestaoDeApelido[]
}

/**
 * Quem monta projeto novo não precisa inventar nome: o mesmo componente já foi
 * batizado em outros projetos. Em produção, 19% dos componentes estão sem
 * apelido.
 */
export function usarSugestoesDeApelido(projetoId: number, somenteSemApelido: boolean) {
  return useQuery({
    queryKey: ["sugestoes-apelido", projetoId, somenteSemApelido] as const,
    enabled: projetoId > 0,
    staleTime: 5 * 60_000,
    queryFn: () => obter<SugestoesDoProjeto>(
      `api/projetos/${projetoId}/importacao/sugestoes-de-apelido`, { somenteSemApelido }),
  })
}

export type ApelidosAplicados = { alterados: number; semMudanca: number }

/**
 * Grava o apelido escolhido.
 *
 * **Sem isto a sugestão só servia para copiar.** A tela mostrava os nomes e
 * mandava a pessoa ir à aba de cadastros colar um por um; o sistema antigo grava
 * com um clique, e era a metade que faltava.
 *
 * Aceita lista porque a rota aceita — hoje a tela manda um de cada vez, que é o
 * gesto de quem está olhando componente por componente e decidindo.
 */
export function usarAplicarApelidos(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (itens: { componenteId: number; apelido: string }[]) =>
      (await api.put<ApelidosAplicados>(
        `api/projetos/${projetoId}/importacao/apelidos`, { itens },
        noProjeto(projetoId))).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sugestoes-apelido", projetoId] })
      // A grade de componentes mostra o apelido numa coluna.
      qc.invalidateQueries({ queryKey: ["componentes"] })
    },
  })
}
